import { Component, DestroyRef, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { EMPTY, catchError, finalize } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiFileUploader } from 'omnium-ui/file-uploader';
import { OuiFileItem } from 'omnium-ui/file-item';
import { OuiTextfield } from 'omnium-ui/form-field';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTableModule } from 'omnium-ui/table';
import { OuiTagModule } from 'omnium-ui/tag';
import { appRoutePaths } from '../../../../app.paths';
import {
  ActionAuthorizationService,
  doublonsBusterActionCodes
} from '../../../../core/auth/action-authorization.service';
import { ExPostCampaignSummary, ExPostImportSummary } from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { StateCardComponent } from '../../shared/state-card/state-card.component';

const CAMPAIGN_YEAR_PATTERN = /(\d{4})/;

@Component({
  selector: 'dbw-couts-ex-post-page',
  imports: [
    FormsModule,
    MatTableModule,
    RouterModule,
    OuiButtonModule,
    OuiFileUploader,
    OuiFileItem,
    OuiTableModule,
    OuiTagModule,
    OuiTextfield,
    StateCardComponent
  ],
  templateUrl: './couts-ex-post.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./couts-ex-post.component.scss']
})
export class CoutsExPostPageComponent {
  readonly displayedColumns = ['year', 'fileName', 'status', 'createdBy', 'createdAt', 'actions'];

  readonly campaigns = signal<ExPostCampaignSummary[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | undefined>(undefined);

  readonly selectedFile = signal<File | null>(null);
  readonly campaignYear = signal<string>('');
  readonly validating = signal(false);
  readonly importing = signal(false);
  readonly validationSummary = signal<ExPostImportSummary | undefined>(undefined);
  readonly awaitingConfirmation = signal(false);

  readonly canImport = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.exPostCampaignImport)
  );

  readonly canSubmit = computed(
    () =>
      this.canImport() &&
      !!this.selectedFile() &&
      this.isYearValid() &&
      !this.validating() &&
      !this.importing()
  );

  readonly isYearValid = computed(() => {
    const year = Number(this.campaignYear());
    return Number.isInteger(year) && year >= 2000 && year <= 2100;
  });

  readonly appRoutePaths = appRoutePaths;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly snackbarService: OuiSnackbarService,
    private readonly router: Router
  ) {
    this.load();
  }

  reload(): void {
    this.load(true);
  }

  onFilesChange(files: File[] | null): void {
    const file = files?.[0] ?? null;
    this.selectedFile.set(file);
    this.resetValidation();

    if (file && !this.campaignYear()) {
      const match = CAMPAIGN_YEAR_PATTERN.exec(file.name);
      if (match) {
        this.campaignYear.set(match[1]);
      }
    }
  }

  removeFile(): void {
    this.selectedFile.set(null);
    this.resetValidation();
  }

  onYearChange(value: string): void {
    this.campaignYear.set(value);
    this.resetValidation();
  }

  validate(): void {
    const file = this.selectedFile();

    if (!file || !this.canSubmit()) {
      return;
    }

    this.validating.set(true);
    this.resetValidation();

    this.apiService
      .validateExPostImport(file, Number(this.campaignYear()))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.validating.set(false))
      )
      .subscribe({
        next: summary => {
          this.validationSummary.set(summary);
          this.awaitingConfirmation.set(true);
        },
        error: httpError => {
          this.snackbarService.open(
            httpError?.error?.detail ?? 'La validation du fichier a échoué.',
            'error'
          );
        }
      });
  }

  confirmImport(): void {
    const file = this.selectedFile();

    if (!file || this.importing()) {
      return;
    }

    this.importing.set(true);

    this.apiService
      .importExPostCampaign(file, Number(this.campaignYear()))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.importing.set(false))
      )
      .subscribe({
        next: summary => {
          this.snackbarService.open(
            `La campagne ${summary.year} a été importée (${summary.emailDocumentCount} bordereaux mail, ${summary.courrierDocumentCount} bordereaux courrier).`,
            'success'
          );

          this.selectedFile.set(null);
          this.campaignYear.set('');
          this.resetValidation();

          if (summary.campaignId) {
            this.router.navigateByUrl(
              this.appRoutePaths.controleGestion.coutsExPostCampaign(summary.campaignId)
            );
          } else {
            this.load(true);
          }
        },
        error: httpError => {
          this.snackbarService.open(
            httpError?.error?.detail ?? "L'import de la campagne a échoué.",
            'error'
          );
        }
      });
  }

  cancelValidation(): void {
    this.resetValidation();
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return 'Non renseigné';
    }

    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(value));
  }

  getStatusLabel(campaign: ExPostCampaignSummary): string {
    switch (campaign.status) {
      case 'Imported':
        return 'Importée';
      case 'Generating':
        return 'Génération en cours';
      case 'Generated':
        return 'Bordereaux générés';
      case 'GeneratedWithErrors':
        return 'Générée avec erreurs';
      case 'Superseded':
        return 'Remplacée';
      default:
        return campaign.status;
    }
  }

  private resetValidation(): void {
    this.validationSummary.set(undefined);
    this.awaitingConfirmation.set(false);
  }

  private load(forceLoadingIndicator = false): void {
    if (forceLoadingIndicator || this.campaigns().length === 0) {
      this.loading.set(true);
    }

    this.apiService
      .getExPostCampaigns()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(httpError => {
          this.error.set(httpError?.error?.detail ?? 'Impossible de charger les campagnes coûts ex-post.');
          this.loading.set(false);
          return EMPTY;
        })
      )
      .subscribe(campaigns => {
        this.campaigns.set(campaigns);
        this.error.set(undefined);
        this.loading.set(false);
      });
  }
}
